/**
 * useTarifs Hook
 * React Query hooks for tarif (pricing matrix) operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTarifs,
  getTarifById,
  getTarifBanques,
  getTarifServices,
  getTarifPrestations,
  getAffaireContext,
  uploadTarifsExcel,
  getTarifImportBatches,
  deleteTarifBatch,
  deactivateAllTarifs,
  getBanqueProvinces
} from '../api/facturation.api';
import type {
  TarifFilters
} from '../types/facturation.types';

// Query keys
export const tarifKeys = {
  all: ['tarifs'] as const,
  lists: () => [...tarifKeys.all, 'list'] as const,
  list: (filters: TarifFilters) => [...tarifKeys.lists(), filters] as const,
  details: () => [...tarifKeys.all, 'detail'] as const,
  detail: (id: number) => [...tarifKeys.details(), id] as const,
  banques: () => [...tarifKeys.all, 'banques'] as const,
  services: () => [...tarifKeys.all, 'services'] as const,
  prestations: (banqueCode: string, serviceCode: string) =>
    [...tarifKeys.all, 'prestations', banqueCode, serviceCode] as const,
  affaireContext: (affaireId: string) => [...tarifKeys.all, 'context', affaireId] as const,
  importBatches: () => [...tarifKeys.all, 'batches'] as const,
  banqueProvinces: () => [...tarifKeys.all, 'banque-provinces'] as const,
};

/**
 * Get all bank-level province defaults (the "RÈGLE BANQUE" Excel row)
 */
export const useBanqueProvinces = () => {
  return useQuery({
    queryKey: tarifKeys.banqueProvinces(),
    queryFn: getBanqueProvinces,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Get all tarifs with optional filters
 */
export const useTarifs = (filters: TarifFilters = {}) => {
  return useQuery({
    queryKey: tarifKeys.list(filters),
    queryFn: () => getTarifs(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get single tarif by ID
 */
export const useTarif = (id: number) => {
  return useQuery({
    queryKey: tarifKeys.detail(id),
    queryFn: () => getTarifById(id),
    enabled: !!id,
  });
};

/**
 * Get distinct banques from tarifs
 * Used for the first dropdown in 3-step selection
 */
export const useTarifBanques = () => {
  return useQuery({
    queryKey: tarifKeys.banques(),
    queryFn: () => getTarifBanques(),
    staleTime: 10 * 60 * 1000, // 10 minutes - banques don't change often
  });
};

/**
 * Get distinct services from tarifs
 * Used for the second dropdown in 3-step selection
 */
export const useTarifServices = () => {
  return useQuery({
    queryKey: tarifKeys.services(),
    queryFn: () => getTarifServices(),
    staleTime: 10 * 60 * 1000, // 10 minutes - services don't change often
  });
};

/**
 * Get prestations for a specific banque and service
 * Used for the third dropdown in 3-step selection
 */
export const useTarifPrestations = (banqueCode: string, serviceCode: string) => {
  return useQuery({
    queryKey: tarifKeys.prestations(banqueCode, serviceCode),
    queryFn: () => getTarifPrestations(banqueCode, serviceCode),
    enabled: !!banqueCode && !!serviceCode,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get remembered banque/service context for an affaire
 * Used to pre-select dropdowns for subsequent product additions
 */
export const useAffaireContext = (affaireId: string) => {
  return useQuery({
    queryKey: tarifKeys.affaireContext(affaireId),
    queryFn: () => getAffaireContext(affaireId),
    enabled: !!affaireId,
    staleTime: 0, // Always refetch on mount for fresh affaire context
  });
};

/**
 * Get import batches (for admin view)
 */
export const useTarifImportBatches = () => {
  return useQuery({
    queryKey: tarifKeys.importBatches(),
    queryFn: () => getTarifImportBatches(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Upload tarifs from Excel file mutation
 */
export const useUploadTarifsExcel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => uploadTarifsExcel(file),
    onSuccess: () => {
      // Invalidate all tarif queries after successful import
      queryClient.invalidateQueries({ queryKey: tarifKeys.all });
    },
  });
};

/**
 * Delete tarifs by batch ID mutation
 */
export const useDeleteTarifBatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (batchId: string) => deleteTarifBatch(batchId),
    onSuccess: () => {
      // Invalidate all tarif queries after batch deletion
      queryClient.invalidateQueries({ queryKey: tarifKeys.all });
    },
  });
};

/**
 * Deactivate all tarifs mutation (before new import)
 */
export const useDeactivateAllTarifs = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deactivateAllTarifs(),
    onSuccess: () => {
      // Invalidate all tarif queries after deactivation
      queryClient.invalidateQueries({ queryKey: tarifKeys.all });
    },
  });
};
